import React, { useState, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, ButtonGroup, Box, Flex, Text, Grid, GridItem,
  IconButton, Input, useBreakpointValue
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { translateColumnDisplayName, translateOperatorLabel } from './filterDisplayHelpers';

const GRANULARITY_KEYS = ['day', 'month', 'quarter', 'half', 'year'];

const currentYear = new Date().getFullYear();
const START_YEAR = 2018;
const END_YEAR = currentYear + 1;

// ── Helpers ──

const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

const pad = (n) => String(n).padStart(2, '0');

const lastDayOfMonth = (year, month) => getDaysInMonth(year, month);

const resolveDate = (selection, operator) => {
  const { year, month, quarter, half, day } = selection;
  const isAfter = operator === 'gte';

  if (day != null) {
    return `${year}-${pad(month + 1)}-${pad(day)}`;
  }
  if (month != null) {
    return isAfter
      ? `${year}-${pad(month + 1)}-01`
      : `${year}-${pad(month + 1)}-${pad(lastDayOfMonth(year, month))}`;
  }
  if (quarter != null) {
    const startMonth = (quarter - 1) * 3;
    const endMonth = startMonth + 2;
    return isAfter
      ? `${year}-${pad(startMonth + 1)}-01`
      : `${year}-${pad(endMonth + 1)}-${pad(lastDayOfMonth(year, endMonth))}`;
  }
  if (half != null) {
    return isAfter
      ? `${year}-01-01`
      : (half === 1 ? `${year}-06-30` : `${year}-12-31`);
  }
  // Year only
  return isAfter ? `${year}-01-01` : `${year}-12-31`;
};

const buildLabel = (selection, monthNames) => {
  const { year, month, quarter, half, day } = selection;
  if (day != null) return `${pad(month + 1)}/${pad(day)}/${year}`;
  if (month != null) return `${monthNames[month]} ${year}`;
  if (quarter != null) return `Q${quarter} ${year}`;
  if (half != null) return `H${half} ${year}`;
  return `${year}`;
};

const parseInitialSelection = (value) => {
  if (!value) return null;
  const parts = value.split('-');
  if (parts.length < 3) return null;
  const y = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10) - 1;
  const d = parseInt(parts[2], 10);
  return { year: y, month: m, day: d };
};

// ── Sub-pickers ──

const YearPicker = ({ selection, onSelect }) => (
  <Box maxH="260px" overflowY="auto" p={2}>
    <Grid templateColumns="repeat(3, 1fr)" gap={2}>
      {Array.from({ length: END_YEAR - START_YEAR + 1 }, (_, i) => END_YEAR - i).map(yr => {
        const selected = selection?.year === yr && selection?.month == null && selection?.quarter == null && selection?.half == null && selection?.day == null;
        return (
          <Button
            key={yr}
            size="sm"
            variant={selected ? 'solid' : 'outline'}
            bg={selected ? 'redesign.heading' : undefined}
            color={selected ? 'redesign.bg.surface' : undefined}
            borderColor="redesign.border.strong"
            _hover={{ bg: selected ? 'redesign.text' : 'redesign.bg.hover' }}
            onClick={() => onSelect({ year: yr })}
          >
            {yr}
          </Button>
        );
      })}
    </Grid>
  </Box>
);

const HalfYearPicker = ({ selection, onSelect }) => (
  <Box maxH="260px" overflowY="auto" p={2}>
    {Array.from({ length: END_YEAR - START_YEAR + 1 }, (_, i) => END_YEAR - i).map(yr => (
      <Box key={yr} mb={3}>
        <Text fontSize="xs" fontWeight="600" color="redesign.text.muted" mb={1}>{yr}</Text>
        <Grid templateColumns="repeat(2, 1fr)" gap={2}>
          {[1, 2].map(h => {
            const selected = selection?.year === yr && selection?.half === h;
            return (
              <Button
                key={h}
                size="sm"
                variant={selected ? 'solid' : 'outline'}
                bg={selected ? 'redesign.heading' : undefined}
                color={selected ? 'redesign.bg.surface' : undefined}
                borderColor="redesign.border.strong"
                _hover={{ bg: selected ? 'redesign.text' : 'redesign.bg.hover' }}
                onClick={() => onSelect({ year: yr, half: h })}
              >
                H{h}
              </Button>
            );
          })}
        </Grid>
      </Box>
    ))}
  </Box>
);

const QuarterPicker = ({ selection, onSelect }) => (
  <Box maxH="260px" overflowY="auto" p={2}>
    {Array.from({ length: END_YEAR - START_YEAR + 1 }, (_, i) => END_YEAR - i).map(yr => (
      <Box key={yr} mb={3}>
        <Text fontSize="xs" fontWeight="600" color="redesign.text.muted" mb={1}>{yr}</Text>
        <Grid templateColumns="repeat(4, 1fr)" gap={2}>
          {[1, 2, 3, 4].map(q => {
            const selected = selection?.year === yr && selection?.quarter === q;
            return (
              <Button
                key={q}
                size="sm"
                variant={selected ? 'solid' : 'outline'}
                bg={selected ? 'redesign.heading' : undefined}
                color={selected ? 'redesign.bg.surface' : undefined}
                borderColor="redesign.border.strong"
                _hover={{ bg: selected ? 'redesign.text' : 'redesign.bg.hover' }}
                onClick={() => onSelect({ year: yr, quarter: q })}
              >
                Q{q}
              </Button>
            );
          })}
        </Grid>
      </Box>
    ))}
  </Box>
);

const MonthPicker = ({ selection, onSelect, monthNames }) => (
  <Box maxH="260px" overflowY="auto" p={2}>
    {Array.from({ length: END_YEAR - START_YEAR + 1 }, (_, i) => END_YEAR - i).map(yr => (
      <Box key={yr} mb={3}>
        <Text fontSize="xs" fontWeight="600" color="redesign.text.muted" mb={1}>{yr}</Text>
        <Grid templateColumns="repeat(3, 1fr)" gap={2}>
          {monthNames.map((m, idx) => {
            const selected = selection?.year === yr && selection?.month === idx;
            return (
              <Button
                key={idx}
                size="sm"
                variant={selected ? 'solid' : 'outline'}
                bg={selected ? 'redesign.heading' : undefined}
                color={selected ? 'redesign.bg.surface' : undefined}
                borderColor="redesign.border.strong"
                _hover={{ bg: selected ? 'redesign.text' : 'redesign.bg.hover' }}
                onClick={() => onSelect({ year: yr, month: idx })}
              >
                {m}
              </Button>
            );
          })}
        </Grid>
      </Box>
    ))}
  </Box>
);

const SingleMonthCalendar = ({ year, month, selection, onSelect, monthNames, dayNames }) => {
  const today = new Date();
  const todayY = today.getFullYear();
  const todayM = today.getMonth();
  const todayD = today.getDate();

  const daysInMonth = getDaysInMonth(year, month);
  const firstDow = new Date(year, month, 1).getDay(); // 0=Sun

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const isSelected = (d) =>
    selection?.year === year && selection?.month === month && selection?.day === d;

  const isToday = (d) => year === todayY && month === todayM && d === todayD;

  return (
    <Box>
      <Text fontSize="sm" fontWeight="600" mb={2} textAlign="center">
        {monthNames[month]} {year}
      </Text>
      <Grid templateColumns="repeat(7, 1fr)" gap={0} mb={1}>
        {dayNames.map(d => (
          <GridItem key={d} textAlign="center">
            <Text fontSize="2xs" color="redesign.text.muted" fontWeight="500">{d}</Text>
          </GridItem>
        ))}
      </Grid>
      <Grid templateColumns="repeat(7, 1fr)" gap={0}>
        {cells.map((d, i) => (
          <GridItem key={i}>
            {d ? (
              <Button
                size="sm"
                variant={isSelected(d) ? 'solid' : 'ghost'}
                bg={isSelected(d) ? 'redesign.heading' : undefined}
                color={isSelected(d) ? 'redesign.bg.surface' : isToday(d) ? 'redesign.accent.blue' : undefined}
                fontWeight={isToday(d) ? '700' : '400'}
                _hover={{ bg: isSelected(d) ? 'redesign.text' : 'redesign.bg.card' }}
                w="100%"
                h="32px"
                minW={0}
                px={0}
                fontSize="sm"
                onClick={() => onSelect({ year, month, day: d })}
              >
                {d}
              </Button>
            ) : (
              <Box h="32px" />
            )}
          </GridItem>
        ))}
      </Grid>
    </Box>
  );
};

const DayPicker = ({ calendarRef, setCalendarRef, selection, onSelect, showTwoMonths, monthNames, dayNames, todayLabel, prevLabel, nextLabel }) => {
  const { year, month } = calendarRef;

  const handlePrev = () => {
    setCalendarRef(prev => {
      const m = prev.month - 1;
      return m < 0 ? { year: prev.year - 1, month: 11 } : { year: prev.year, month: m };
    });
  };

  const handleNext = () => {
    setCalendarRef(prev => {
      const m = prev.month + 1;
      return m > 11 ? { year: prev.year + 1, month: 0 } : { year: prev.year, month: m };
    });
  };

  const handleToday = () => {
    const now = new Date();
    setCalendarRef({ year: now.getFullYear(), month: now.getMonth() });
  };

  // Compute second month
  const secondMonth = month === 11 ? 0 : month + 1;
  const secondYear = month === 11 ? year + 1 : year;

  return (
    <Box p={2}>
      <Flex justify="space-between" align="center" mb={3}>
        <IconButton
          icon={<ChevronLeftIcon />}
          size="sm"
          variant="ghost"
          onClick={handlePrev}
          aria-label={prevLabel}
        />
        <Button size="xs" variant="ghost" onClick={handleToday}>{todayLabel}</Button>
        <IconButton
          icon={<ChevronRightIcon />}
          size="sm"
          variant="ghost"
          onClick={handleNext}
          aria-label={nextLabel}
        />
      </Flex>
      <Flex gap={4} direction={showTwoMonths ? 'row' : 'column'}>
        <Box flex={1}>
          <SingleMonthCalendar year={year} month={month} selection={selection} onSelect={onSelect} monthNames={monthNames} dayNames={dayNames} />
        </Box>
        {showTwoMonths && (
          <Box flex={1}>
            <SingleMonthCalendar year={secondYear} month={secondMonth} selection={selection} onSelect={onSelect} monthNames={monthNames} dayNames={dayNames} />
          </Box>
        )}
      </Flex>
    </Box>
  );
};

// ── Main Component ──

const AdvancedDatePicker = ({ isOpen, onClose, filter, initialOperator, initialValue, onAddFilter }) => {
  const { t } = useTranslation('filters');
  const monthsShort = t('advancedPicker.monthsShort', { returnObjects: true });
  const dayNamesShort = t('advancedPicker.dayNamesShort', { returnObjects: true });
  const [operator, setOperator] = useState(initialOperator || 'gte');
  const [granularity, setGranularity] = useState('day');
  const [selection, setSelection] = useState(() => parseInitialSelection(initialValue));
  const [calendarRef, setCalendarRef] = useState(() => {
    const parsed = parseInitialSelection(initialValue);
    if (parsed) return { year: parsed.year, month: parsed.month };
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });

  const showTwoMonths = useBreakpointValue({ base: false, md: true });

  const handleGranularityChange = useCallback((g) => {
    setGranularity(g);
    setSelection(null);
  }, []);

  const handleSelect = useCallback((sel) => {
    setSelection(sel);
  }, []);

  const displayValue = selection ? buildLabel(selection, monthsShort) : '';

  const handleApply = () => {
    if (!selection) return;
    const resolved = resolveDate(selection, operator);
    const label = buildLabel(selection, monthsShort);
    onAddFilter({
      entity: filter.entity,
      column: filter.column,
      operator,
      values: [resolved],
      presetLabel: label,
    });
    onClose();
  };

  const renderPicker = () => {
    switch (granularity) {
      case 'year':
        return <YearPicker selection={selection} onSelect={handleSelect} />;
      case 'half':
        return <HalfYearPicker selection={selection} onSelect={handleSelect} />;
      case 'quarter':
        return <QuarterPicker selection={selection} onSelect={handleSelect} />;
      case 'month':
        return <MonthPicker selection={selection} onSelect={handleSelect} monthNames={monthsShort} />;
      case 'day':
      default:
        return (
          <DayPicker
            calendarRef={calendarRef}
            setCalendarRef={setCalendarRef}
            selection={selection}
            onSelect={handleSelect}
            showTwoMonths={showTwoMonths}
            monthNames={monthsShort}
            dayNames={dayNamesShort}
            todayLabel={t('advancedPicker.today')}
            prevLabel={t('advancedPicker.previousMonth')}
            nextLabel={t('advancedPicker.nextMonth')}
          />
        );
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md" pb={2}>
          <Text fontSize="sm" color="redesign.text.muted" mb={1}>
            {translateColumnDisplayName(filter.entity, filter.column, filter.displayName)}
          </Text>
          <ButtonGroup size="xs" isAttached variant="outline">
            <Button
              bg={operator === 'lte' ? 'redesign.heading' : 'transparent'}
              color={operator === 'lte' ? 'redesign.bg.surface' : 'redesign.text'}
              borderColor={operator === 'lte' ? 'redesign.heading' : 'redesign.border.strong'}
              _hover={{ bg: operator === 'lte' ? 'redesign.text' : 'redesign.bg.hover' }}
              onClick={() => setOperator('lte')}
            >
              {translateOperatorLabel('lte', 'date')}
            </Button>
            <Button
              bg={operator === 'gte' ? 'redesign.heading' : 'transparent'}
              color={operator === 'gte' ? 'redesign.bg.surface' : 'redesign.text'}
              borderColor={operator === 'gte' ? 'redesign.heading' : 'redesign.border.strong'}
              _hover={{ bg: operator === 'gte' ? 'redesign.text' : 'redesign.bg.hover' }}
              onClick={() => setOperator('gte')}
            >
              {translateOperatorLabel('gte', 'date')}
            </Button>
          </ButtonGroup>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pt={0}>
          {/* Read-only input showing current selection */}
          <Input
            size="sm"
            value={displayValue}
            placeholder={t('advancedPicker.selectDate')}
            isReadOnly
            mb={3}
          />

          {/* Granularity tabs */}
          <Flex gap={1} mb={3} flexWrap="wrap">
            {GRANULARITY_KEYS.map(key => (
              <Button
                key={key}
                size="xs"
                variant={granularity === key ? 'solid' : 'outline'}
                bg={granularity === key ? 'redesign.heading' : undefined}
                color={granularity === key ? 'redesign.bg.surface' : undefined}
                borderColor="redesign.border.strong"
                _hover={{ bg: granularity === key ? 'redesign.text' : 'redesign.bg.hover' }}
                onClick={() => handleGranularityChange(key)}
              >
                {t(`advancedPicker.granularities.${key}`)}
              </Button>
            ))}
          </Flex>

          {/* Picker body */}
          <Box
            border="1px solid"
            borderColor="redesign.border"
            borderRadius="md"
            maxH="300px"
            overflowY="auto"
          >
            {renderPicker()}
          </Box>
        </ModalBody>

        <ModalFooter pt={3}>
          <Button size="sm" variant="ghost" mr={2} onClick={onClose}>{t('advancedPicker.cancel')}</Button>
          <Button
            size="sm"
            bg="redesign.heading"
            color="redesign.bg.surface"
            _hover={{ bg: 'redesign.text' }}
            onClick={handleApply}
            isDisabled={!selection}
          >
            {t('advancedPicker.apply')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AdvancedDatePicker;
